import React, { useState, useRef, useEffect } from 'react';
import _get from 'lodash/get';
import styles from './index.module.scss';

import { UiKit } from 'components';
import { isValidEmail } from 'utils/validators';
import EmailBadges from './EmailBadges';

export default function MailingList({
  label = '',
  caption = '',
  emails = {},
  error = '',
  disabled = false,
  focusOnMount = false,
  onUpdate = () => {},
  placeholder = 'Add e-mail address and press enter ⏎',
  emptyBlockMsg = 'No recipients added.'
}) {
  const [email, setEmail] = useState('');
  const [isPasted, setIsPasted] = useState(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState('');

  const inputRef = useRef(null);
  const pasteDataRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      const onPaste = e => {
        const emails = e.clipboardData.getData('text/plain');
        pasteDataRef.current = emails.split(',').reduce(
          (acc, email) => {
            const emailAddress = email.trim();
            const isValid = isValidEmail(emailAddress);
            if (isValid) {
              acc.validEmailsData[emailAddress] = true;
            } else {
              acc.invalidEmails.push(emailAddress);
            }
            return acc;
          },
          { validEmailsData: {}, invalidEmails: [] }
        );
        setIsPasted(true);
      };
      inputRef.current.addEventListener('paste', onPaste);
      return () => document.removeEventListener('paste', onPaste);
    }
  }, []);

  useEffect(() => {
    if (focusOnMount && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focusOnMount]);

  useEffect(() => {
    if (isPasted) {
      const timer = setTimeout(() => {
        const emailsData = _get(pasteDataRef.current, 'validEmailsData', {});
        const invalidEmails = _get(pasteDataRef.current, 'invalidEmails', []);
        const inputText = invalidEmails.join(', ');
        setEmail(inputText);
        setIsEmailInvalid(!!inputText);
        onUpdate({ ...emailsData, ...emails });
        setIsPasted(false);
        clearTimeout(timer);
      }, 0);
    }
  }, [isPasted, emails, onUpdate]);

  const inputChangeHandler = value => {
    setEmail(value);
    setIsEmailInvalid(false);
  };

  const addEmail = email => {
    if (disabled) return;
    onUpdate({ [email]: true, ...emails });
  };

  const deleteEmail = mail => {
    if (disabled) return;
    const addedMails = { ...emails };
    delete addedMails[mail];
    onUpdate(addedMails);
  };

  const pickEmailAddress = e => {
    // if user presses comma (188) on keyboard || enter (13) key
    if (e.which === 188 || e.which === 13) {
      const email = e.target.value
        .trim()
        .split(',')
        .join('');
      const isEmailValid = isValidEmail(email);
      setIsEmailInvalid(!isEmailValid);
      if (isEmailValid) {
        setEmail('');
        addEmail(email);
      }
    }
  };

  const errorMessage = isEmailInvalid
    ? 'Please enter a valid email address'
    : error;

  return (
    <div className={styles.container}>
      <UiKit.Input
        type="email"
        label={label}
        caption={caption}
        value={email}
        inputRef={inputRef}
        disabled={disabled}
        placeholder={placeholder}
        onKeyUp={pickEmailAddress}
        onChange={inputChangeHandler}
        error={!!errorMessage}
        inputClassName={styles.input}
      />
      <div className={styles.content} data-disabled={disabled}>
        <UiKit.Error msg={errorMessage} hide={disabled} />
        <EmailBadges
          emails={Object.keys(emails)}
          disabled={disabled}
          onDelete={deleteEmail}
          emptyBlockMsg={emptyBlockMsg}
        />
      </div>
    </div>
  );
}
